var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { logout as clearSession } from "@services/AuthService";
import { fetchUserDetailsOnLogIn, LogoutApi } from "@services/user/UserService";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setGlobalLocale, setUserInfo, useBEOController, } from "@context/BeoContext";
import { fetchMessage } from "@services/common/MessageService";
import { getUserName, getUserRole } from "@utils/auth";
import i18n from "../i18n";
import { useThemeSwitcher } from "./ThemeSwitcher";
/**
 * Custom hook for initializing the application state, including user details,
 * theme, language, and menu items. It handles authentication checks,
 * data fetching, and provides logout functionality.
 *
 * @hook
 * @returns {object} An object containing:
 * @property {boolean} loading - Indicates if the initial application loading is in progress.
 * @property {() => Promise<void>} handleLogout - A memoized function to handle user logout.
 * @property {any[]} menuDetails - The raw menu data fetched from the API.
 * @property {() => Promise<void>} refreshMenu - A memoized function to re-fetch menu details.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var useAppInitialization = function () {
    /**
     * Retrieves the global BEO context and dispatch function.
     * @type {Array<object, function>}
     */
    var _a = useBEOController(), context = _a[0], dispatch = _a[1];
    /**
     * React Router hook for accessing current location information.
     * @type {object}
     */
    var location = useLocation();
    /**
     * Stores the current user information from context for comparison.
     * @type {object | undefined}
     */
    var currentUserInfo = context.userInfo;
    /**
     * Stores the current theme from context for comparison.
     * @type {string | undefined}
     */
    var currentTheme = context.theme;
    /**
     * Ref to track whether initial data fetching has already occurred.
     * Prevents re-fetching on subsequent renders if not necessary.
     * @type {React.MutableRefObject<boolean>}
     * @private
     */
    var hasFetchedData = useRef(false);
    /**
     * Checks if the current path indicates the login page.
     * @type {boolean}
     * @private
     */
    var isLoginPage = location.pathname.includes("/login");
    /**
     * This endpoint used to find user roles and permissions
     */
    var USER_ENDPOINT_API = "/userendpoints/find";
    var navigate = useNavigate();
    var applyTheme = useThemeSwitcher().applyTheme;
    useEffect(function () {
        // Do not initialize if on the login page or if data has already been fetched
        var fetchData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var route;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // Do not initialize if on the login page or if data has already been fetched
                        if (isLoginPage || hasFetchedData.current)
                            return [2 /*return*/];
                        hasFetchedData.current = true;
                        return [4 /*yield*/, initialMethod()];
                    case 1:
                        route = _a.sent();
                        navigate(route);
                        return [2 /*return*/];
                }
            });
        }); };
        fetchData();
    }, [isLoginPage]);
    /**
     * Memoized callback function to handle user logout.
     * It calls the backend logout API, clears session storage, and resets
     * global context states related to user and menu.
     * @returns {Promise<void>}
     */
    var handleLogout = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    if (!getUserName()) return [3 /*break*/, 2];
                    return [4 /*yield*/, LogoutApi({
                            onSuccess: function (response) {
                                console.log("response is " + response);
                                if (response.responseBody === "User logged out successfully") {
                                    clearSession();
                                    hasFetchedData.current = false; // Reset flag to allow re-init on next login
                                    setUserInfo(dispatch, null); // Clear user
                                }
                            },
                            onError: function (error) {
                                console.log(error.message);
                            },
                        })];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2: return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    console.log(error_1.message);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [getUserName(), dispatch]);
    var initialMethod = function () { return __awaiter(void 0, void 0, void 0, function () {
        var authenticated, role, role_1, error_2;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getUserDetails()];
                case 1:
                    authenticated = _c.sent();
                    role = getUserRole();
                    if (authenticated &&
                        (((_b = (_a = context === null || context === void 0 ? void 0 : context.userInfo) === null || _a === void 0 ? void 0 : _a.roles) === null || _b === void 0 ? void 0 : _b.length) !== 0 || role === "ADMIN")) {
                        role_1 = getUserRole();
                        console.info("role " + role_1);
                        // Redirect based on role
                        if (role_1 === "ADMIN") {
                            console.log("ROLE_ADMIN role ");
                            return [2 /*return*/, "/admin/dashboard"];
                        }
                        else if (role_1 === "MANDANT") {
                            console.log("MANDANT");
                            return [2 /*return*/, "/client/dashboard"];
                        }
                        else if (role_1 === "USER") {
                            return [2 /*return*/, "/user/dashboard"];
                        }
                        else {
                            return [2 /*return*/, "/unauthorized"];
                        }
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _c.sent();
                    console.error("App initialization failed:", error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/, "/unauthorized"];
            }
        });
    }); };
    /**
     * Memoized callback function to fetch user details.
     * It retrieves user information from the backend, parses user features if necessary,
     * updates the global user context, sets the global locale, and applies the user's preferred theme.
     * @returns {Promise<boolean>} A promise that resolves to `true` if authentication data is successfully fetched and processed, `false` otherwise.
     */
    var getUserDetails = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var storedUsername, response, userData, rolesDetails, lang_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    storedUsername = getUserName();
                    console.log("storedUsername " + storedUsername);
                    if (!storedUsername)
                        return [2 /*return*/];
                    return [4 /*yield*/, fetchUserDetailsOnLogIn({
                            userId: storedUsername,
                        })];
                case 1:
                    response = _a.sent();
                    if (!(response === null || response === void 0 ? void 0 : response.responseBody)) return [3 /*break*/, 4];
                    userData = __assign({}, response.responseBody);
                    if (!!isEqual(currentUserInfo, userData)) return [3 /*break*/, 3];
                    return [4 /*yield*/, fetchMessage({ userId: getUserName() }, USER_ENDPOINT_API)];
                case 2:
                    rolesDetails = _a.sent();
                    console.log(JSON.stringify(rolesDetails));
                    setUserInfo(dispatch, {
                        username: userData.username,
                        client: userData.userId,
                        theme: userData.theme,
                        language: userData.language,
                        roles: rolesDetails,
                    });
                    _a.label = 3;
                case 3:
                    lang_1 = userData.language;
                    i18n.changeLanguage(lang_1, function () { return setGlobalLocale(dispatch, lang_1); });
                    console.log(" userData.theme " + userData.theme);
                    if (userData.theme && currentTheme !== userData.theme) {
                        applyTheme(userData.theme);
                    }
                    else {
                        applyTheme(currentTheme);
                    }
                    return [2 /*return*/, true]; // ✅ Auth success
                case 4: return [2 /*return*/, false];
            }
        });
    }); }, [dispatch, i18n, currentUserInfo, currentTheme, applyTheme]);
    return {
        initialMethod: initialMethod,
        handleLogout: handleLogout,
    };
};
